// Minimal natural-language parser for quick-add fields.
// Handles the phrasings that cover ~90% of real usage:
//   - "tomorrow 7pm", "tonight", "today at 15:30"
//   - "mon", "monday", "next friday"
//   - "in 2 hours", "in 30 mins"
//   - trailing "#list" and "@assignee" hashtag-style meta
// Intentionally no new dependency — chrono-node covers more but costs 50kB.

export interface ParsedQuickAdd {
  title: string;
  date?: string;   // YYYY-MM-DD
  time?: string;   // HH:MM (24h)
  list?: string;   // bare word after "#"
  assignee?: string; // bare word after "@"
}

const pad = (n: number) => n.toString().padStart(2, '0');
const toDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const DAY_NAMES: Record<string, number> = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tues: 2, tuesday: 2,
  wed: 3, weds: 3, wednesday: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
};

const stripRange = (text: string, start: number, len: number) =>
  (text.slice(0, start) + text.slice(start + len)).replace(/\s{2,}/g, ' ').trim();

const matchTime = (text: string): { time: string; start: number; len: number } | null => {
  // 7pm, 7:30pm, 07:30, 19:00, 7 pm
  const re = /\b(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?\b/i;
  const m = text.match(re);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mm = m[2] ? parseInt(m[2], 10) : 0;
  const suf = (m[3] || '').toLowerCase().replace(/\./g, '');
  // Require meridiem OR a colon — otherwise "7" alone is too ambiguous.
  if (!suf && !m[2]) return null;
  if (suf === 'pm' && h < 12) h += 12;
  if (suf === 'am' && h === 12) h = 0;
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return { time: `${pad(h)}:${pad(mm)}`, start: m.index ?? 0, len: m[0].length };
};

const matchRelative = (text: string, now: Date): { date: string; time?: string; start: number; len: number } | null => {
  const lower = text.toLowerCase();

  // "in 2 hours", "in 30 minutes"
  const inM = lower.match(/\bin\s+(\d+)\s*(min|mins|minute|minutes|hr|hrs|hour|hours)\b/);
  if (inM) {
    const n = parseInt(inM[1], 10);
    const isHour = /^h/.test(inM[2]);
    const target = new Date(now.getTime() + n * (isHour ? 3600_000 : 60_000));
    return { date: toDate(target), time: `${pad(target.getHours())}:${pad(target.getMinutes())}`, start: inM.index!, len: inM[0].length };
  }

  // "today" / "tonight" / "tomorrow" / "tmrw"
  const wordRe = /\b(today|tonight|tomorrow|tmrw|tmr)\b/;
  const wm = lower.match(wordRe);
  if (wm) {
    const word = wm[1];
    const target = new Date(now);
    if (word === 'tomorrow' || word === 'tmrw' || word === 'tmr') target.setDate(target.getDate() + 1);
    const impliedTime = word === 'tonight' ? '19:00' : undefined;
    return { date: toDate(target), time: impliedTime, start: wm.index!, len: wm[0].length };
  }

  // "next monday" / "this friday" / bare "mon"
  const dayRe = /\b(next|this)?\s*(sun|sunday|mon|monday|tue|tues|tuesday|wed|weds|wednesday|thu|thur|thurs|thursday|fri|friday|sat|saturday)\b/;
  const dm = lower.match(dayRe);
  if (dm) {
    const qualifier = (dm[1] || '').trim();
    const target = new Date(now);
    const want = DAY_NAMES[dm[2]];
    const today = target.getDay();
    let diff = (want - today + 7) % 7;
    if (diff === 0) diff = qualifier === 'next' ? 7 : 7; // default to next week for bare day names referencing today
    if (qualifier === 'next' && diff < 7) diff += 7;
    target.setDate(target.getDate() + diff);
    return { date: toDate(target), start: dm.index!, len: dm[0].length };
  }

  return null;
};

const matchHashtag = (text: string, sigil: '#' | '@'): { value: string; start: number; len: number } | null => {
  const re = new RegExp(`(^|\\s)${sigil}([A-Za-z0-9][A-Za-z0-9_-]*)`);
  const m = text.match(re);
  if (!m) return null;
  const start = (m.index ?? 0) + m[1].length;
  return { value: m[2], start, len: sigil.length + m[2].length };
};

export const parseQuickAdd = (raw: string, now: Date = new Date()): ParsedQuickAdd => {
  let text = raw.trim();
  if (!text) return { title: '' };

  const result: ParsedQuickAdd = { title: '' };

  // Extract tags first so they don't bleed into the title
  const hash = matchHashtag(text, '#');
  if (hash) { result.list = hash.value; text = stripRange(text, hash.start, hash.len); }
  const at = matchHashtag(text, '@');
  if (at) { result.assignee = at.value; text = stripRange(text, at.start, at.len); }

  // Relative date words
  const rel = matchRelative(text, now);
  if (rel) {
    result.date = rel.date;
    if (rel.time) result.time = rel.time;
    text = stripRange(text, rel.start, rel.len);
  }

  // Explicit time (wins over implied "tonight" time)
  const tm = matchTime(text);
  if (tm) {
    result.time = tm.time;
    text = stripRange(text, tm.start, tm.len);
  }

  // Leftover "at"/"on"/"@" connectors are noise once fields are extracted
  text = text.replace(/\b(at|on)\b\s*$/i, '').replace(/\s+(at|on)\s+$/i, ' ').trim();

  result.title = text.replace(/\s{2,}/g, ' ').trim();
  return result;
};
